import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ClientStore, Order, StoreLastUpdateResponse } from '../stores/stores';
import { CheckoutService } from './store/store-checkout/checkout.service';
import { LocationService } from './location.service';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { getSelectedStore } from './store/+state/stores.selectors';
import { takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { InitialViewOrchestratorService } from './store/services/initial-view-orchestrator.service';

@Injectable({
  providedIn: 'root',
})
export class OrderService implements OnDestroy {
  selectedStore: ClientStore;

  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private checkoutService: CheckoutService,
    private locationService: LocationService,
    private route: ActivatedRoute,
    private store: Store,
    private initialViewOrchestrator: InitialViewOrchestratorService,
  ) {
    this.store
      .select(getSelectedStore)
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedStore) => {
        this.selectedStore = selectedStore;
      });
  }

  getInitOrder(): Order {
    const order: Order = {
      orderItems: [],
    };

    // Race-safe fast-path for kiosk Eat-In / Take-Away pickup-only stores.
    //
    // `this.selectedStore` is populated by an async subscription to the NgRx
    // `selectedStore` slice and is `undefined` until the first emission
    // arrives. Kiosk-restart flows (inactivity timer end, kiosk-video tap,
    // thank-you-page auto-return-to-start-screen) dispatch `InitializeOrder`
    // shortly after re-fetching the store, which means callers can race
    // ahead of the subscription and reach `getInitOrder()` with
    // `selectedStore` still undefined. The `else if` chain below would then
    // short-circuit to `false` (the `CheckoutService.ifOnly*()` predicates
    // guard `selectedStore` and return `false` when it's missing) and
    // historically NPE on `selectedStore.settings.DEFAULT_DELIVERY_MODE`.
    // Either way the resulting Order was persisted with
    // `delivery_method = NULL` (see card #9664).
    //
    // The orchestrator now maintains a per-store-alias sessionStorage flag
    // recording the "Eat-In / Take-Away pickup-only" verdict, set by
    // `StoreLoadingComponent` on every successful settings load. Reading it
    // here guarantees the right default regardless of where the NgRx
    // subscription is in its lifecycle.
    if (
      this.selectedStore
        ? this.initialViewOrchestrator.isEatInTakeAwayPickupOnly(
            this.selectedStore,
          )
        : this.initialViewOrchestrator.isEatInTakeAwayPickupOnlyForCurrentSession()
    ) {
      order.deliveryMethod = 'NO_LOCATION';
      this.applyLocationIdIfPresent(order);
      return order;
    }

    if (
      this.checkoutService.ifOnlyInStore() ||
      this.locationService.getUrlStoreLocation()
    ) {
      order.deliveryMethod = 'IN_STORE_LOCATION';
    } else if (this.checkoutService.ifOnlySelfPickup()) {
      order.deliveryMethod = 'NO_LOCATION';
    } else if (this.checkoutService.ifOnlyDeliveryToAddress()) {
      order.deliveryMethod = 'ADDRESS';
    } else if (
      this.selectedStore?.settings?.DEFAULT_DELIVERY_MODE &&
      this.checkoutService.ifDeliveryMethodEnabled(
        this.selectedStore.settings.DEFAULT_DELIVERY_MODE,
      )
    ) {
      order.deliveryMethod = this.selectedStore.settings.DEFAULT_DELIVERY_MODE;
    } else if (
      this.selectedStore?.settings?.ORDER_CAPTURE_ASK_EAT_IN_TAKEAWAY &&
      !this.selectedStore?.settings?.DELIVERY_ADDRESS
    ) {
      // Eat-In / Take-Away selector flow is pickup-only by design; ensure the
      // resulting order is persisted with a non-null delivery_method so the
      // admin Orders list can render the Pickup pill.
      order.deliveryMethod = 'NO_LOCATION';
    }
    this.applyLocationIdIfPresent(order);
    const salesPointId = this.selectedStore?.salesPointResolution?.salesPointId;
    if (salesPointId) {
      order.salesPointId = salesPointId;
    }
    return order;
  }

  private applyLocationIdIfPresent(order: Order): void {
    const locationParam = this.route.snapshot.paramMap.get('locationid');
    if (locationParam) {
      order.locationId = parseInt(locationParam, 10);
    }
  }

  getStoreLastUpdate(
    storeId: number,
    orderUuid: string,
    updateType: 'ORDER',
  ): Observable<StoreLastUpdateResponse> {
    return this.http.get<StoreLastUpdateResponse>(
      `/api/v1/stores/${storeId}/orders/${orderUuid}/last-updates/${updateType}`,
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
